import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { docsApi, draftsApi, mattersApi, researchApi, precedentsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Scroll, Sparkle, TrashSimple, Scales, MagnifyingGlass, ThumbsUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import VoiceButton from "@/components/VoiceButton";
import { ProgressTimeline } from "@/components/ProgressTimeline";
import HelpButton from "@/components/HelpButton";

// Poll a draft job until completed/failed. ~5min ceiling at 3s intervals = 100 ticks.
async function pollDraftJob(jobId, { intervalMs = 3000, maxTicks = 100, onTick } = {}) {
    for (let i = 0; i < maxTicks; i++) {
        const j = await draftsApi.jobStatus(jobId);
        onTick?.(j, i);
        if (j.status === "completed") return j;
        if (j.status === "failed") {
            const err = new Error(j.error || "Draft generation failed");
            err.status = j.error_status_code;
            throw err;
        }
        await new Promise((r) => setTimeout(r, intervalMs));
    }
    const err = new Error(
        "Draft is still being generated in the background. You can close this dialog — your draft will appear on the Drafts page in a moment."
    );
    err.softTimeout = true;
    throw err;
}

export default function Drafting() {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const wantNew = sp.get("new") === "1";
    const matterId = sp.get("matter");
    const [drafts, setDrafts] = useState([]);
    const [open, setOpen] = useState(wantNew);

    const reload = () => draftsApi.list().then(setDrafts).catch(() => {});

    useEffect(() => {
        reload();
    }, []);

    const remove = async (id) => {
        try {
            await draftsApi.remove(id);
            setDrafts((arr) => arr.filter((d) => d.id !== id));
            toast.success("Draft deleted");
        } catch {
            toast.error("Failed to delete draft");
        }
    };

    return (
        <div className="h-[calc(100vh-57px)] flex flex-col" data-testid="drafting-page">
            <div className="flex items-center justify-between px-4 md:px-8 py-4 border-b border-border bg-white/70">
                <div className="rule-line pt-2">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Drafting</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-3xl tracking-tight">Drafts</h1>
                    <HelpButton topic="drafting" testid="help-drafting" />
                </div>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="open-generate-draft"><Plus size={14} /> Generate new draft</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-2xl">Generate draft</DialogTitle>
                            <DialogDescription>
                                Pick a draft type, optionally link a matter (its ready documents auto-attach), then describe what you want drafted. High-quality output uses Claude Sonnet 4.5 and can take 60–120 seconds.
                            </DialogDescription>
                        </DialogHeader>
                        <GenerateForm
                            defaultMatterId={matterId}
                            onDone={(d) => {
                                setOpen(false);
                                navigate(`/drafting/${d.id}`);
                            }}
                        />
                    </DialogContent>
                </Dialog>
            </div>
            <div className="flex-1 overflow-y-auto p-4 md:p-8 max-w-[1400px] mx-auto w-full">
                {!drafts.length ? (
                    <div className="text-center border border-dashed rounded-md p-10">
                        <Scroll size={28} className="mx-auto text-muted-foreground mb-2" weight="duotone" />
                        <div className="font-serif text-2xl mb-1">No drafts yet</div>
                        <div className="text-sm text-muted-foreground">Click "Generate new draft" to begin.</div>
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {drafts.map((d) => (
                            <div
                                key={d.id}
                                className="relative group border border-border bg-white rounded-md p-5 hover:shadow-sm hover:border-primary/40 transition-all"
                                data-testid={`draft-card-${d.id}`}
                            >
                                <Link to={`/drafting/${d.id}`} className="block pr-8">
                                    <div className="text-xs text-muted-foreground mb-1 uppercase tracking-[0.2em]">
                                        {d.draft_type?.replace(/_/g, " ")}
                                    </div>
                                    <div className="font-serif text-xl leading-tight line-clamp-2">{d.title}</div>
                                    <div className="text-xs text-muted-foreground mt-2">
                                        v{d.current_version} · {new Date(d.updated_at).toLocaleString()}
                                    </div>
                                </Link>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <button
                                            className="absolute top-3 right-3 p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                            data-testid={`draft-delete-${d.id}`}
                                            aria-label="Delete draft"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <TrashSimple size={16} weight="duotone" />
                                        </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent data-testid="draft-delete-dialog">
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                "{d.title}" and all its versions will be permanently removed. This cannot be undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel data-testid="draft-delete-cancel">Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => remove(d.id)}
                                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                data-testid="draft-delete-confirm"
                                            >
                                                Delete
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const SUGGESTED_REVIEW_TYPES = new Set([
    "legal_notice", "plaint", "written_statement", "bail_application",
    "synopsis", "written_submissions", "rejoinder", "slp_outline",
    "arguments", "reply_to_notice",
]);

export function GenerateForm({ defaultMatterId, onDone }) {
    const [types, setTypes] = useState([]);
    const [matters, setMatters] = useState([]);
    const [docs, setDocs] = useState([]);
    const [memos, setMemos] = useState([]);
    const [v, setV] = useState(() => {
        // Pick up any prefill set by ClientDetail's "Draft engagement letter"
        try {
            const raw = sessionStorage.getItem("draft_prefill");
            if (raw) {
                sessionStorage.removeItem("draft_prefill");
                const prefill = JSON.parse(raw);
                return {
                    draft_type: prefill.draft_type || "legal_notice",
                    instructions: prefill.instructions || "",
                    matter_id: defaultMatterId || "none",
                    language: "en",
                    strict_mode: false,
                    include_document_ids: [],
                    include_research_memo_id: "none",
                    custom_title: prefill.custom_title || "",
                    senior_review: false,
                };
            }
        } catch { /* fall through */ }
        return {
            draft_type: "legal_notice",
            instructions: "",
            matter_id: defaultMatterId || "none",
            language: "en",
            strict_mode: false,
            include_document_ids: [],
            include_research_memo_id: "none",
            custom_title: "",
            senior_review: false,
        };
    });
    const [loading, setLoading] = useState(false);
    const [job, setJob] = useState(null);

    useEffect(() => {
        Promise.all([draftsApi.types(), mattersApi.list(), researchApi.memos()]).then(([t, m, mm]) => {
            setTypes(t);
            setMatters(m);
            setMemos(mm);
        });
    }, []);

    useEffect(() => {
        if (v.matter_id && v.matter_id !== "none") {
            docsApi.list(v.matter_id).then(setDocs).catch(() => setDocs([]));
        } else {
            setDocs([]);
        }
    }, [v.matter_id]);

    const readyDocs = docs.filter((d) => d.processing_status === "ready");
    const noMatter = !v.matter_id || v.matter_id === "none";

    const gen = async () => {
        if (!v.instructions.trim()) {
            toast.error("Please describe what you want drafted");
            return;
        }
        setLoading(true);
        setJob(null);
        try {
            const payload = {
                ...v,
                matter_id: v.matter_id === "none" ? null : v.matter_id,
                include_research_memo_id:
                    v.include_research_memo_id === "none" ? null : v.include_research_memo_id,
            };
            const submitted = await draftsApi.generate(payload);
            // Seed the timeline immediately from the queued job (avoids "no stages" flicker)
            const seed = await draftsApi.jobStatus(submitted.job_id);
            setJob(seed);
            const completed = await pollDraftJob(submitted.job_id, {
                onTick: (j) => setJob(j),
            });
            const draft = await draftsApi.get(completed.draft_id);
            toast.success(v.senior_review ? "Draft generated with Senior review" : "Draft generated");
            onDone?.(draft);
        } catch (e) {
            console.error(e);
            const msg =
                e?.status === 402
                    ? e.message
                    : e?.response?.data?.detail || e?.message || "Draft generation failed";
            toast.error(msg, { duration: 8000 });
        }
        setLoading(false);
    };

    return (
        <div className="space-y-3" data-testid="draft-generate-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Labeled label="Draft type">
                    <Select value={v.draft_type} onValueChange={(val) => setV({ ...v, draft_type: val })}>
                        <SelectTrigger data-testid="draft-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Labeled>
                <Labeled label="Language">
                    <Select value={v.language} onValueChange={(val) => setV({ ...v, language: val })}>
                        <SelectTrigger data-testid="draft-lang-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="en">English</SelectItem>
                            <SelectItem value="hi">हिन्दी</SelectItem>
                            <SelectItem value="mixed">Mixed HI + EN</SelectItem>
                        </SelectContent>
                    </Select>
                </Labeled>
                <Labeled label="Link matter (optional)">
                    <Select value={v.matter_id} onValueChange={(val) => setV({ ...v, matter_id: val, include_document_ids: [] })}>
                        <SelectTrigger data-testid="draft-matter-select"><SelectValue placeholder="(none)" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">No matter</SelectItem>
                            {matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Labeled>
                <Labeled label="Attach research memo (optional)">
                    <Select value={v.include_research_memo_id} onValueChange={(val) => setV({ ...v, include_research_memo_id: val })}>
                        <SelectTrigger data-testid="draft-memo-select"><SelectValue placeholder="(none)" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">No memo</SelectItem>
                            {memos.map((m) => <SelectItem key={m.id} value={m.id}>{m.query.slice(0, 60)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Labeled>
            </div>

            {/* Document attachment guidance — explicit so users understand
                document context is automatically used when they pick a matter. */}
            <Labeled label="Documents as source">
                {noMatter ? (
                    <div
                        className="text-xs text-muted-foreground border border-dashed border-border rounded-sm p-3 bg-muted/30"
                        data-testid="docs-hint-no-matter"
                    >
                        Pick a matter above to make its uploaded documents available as source material.
                    </div>
                ) : !docs.length ? (
                    <div
                        className="text-xs text-muted-foreground border border-dashed border-border rounded-sm p-3 bg-muted/30"
                        data-testid="docs-hint-empty"
                    >
                        No documents uploaded for this matter yet. Upload PDFs in the matter workspace and they'll appear here.
                    </div>
                ) : (
                    <div className="border border-border rounded-sm p-2" data-testid="docs-picker">
                        <div className="text-[11px] text-muted-foreground mb-2 px-1">
                            {v.include_document_ids.length > 0
                                ? `${v.include_document_ids.length} of ${readyDocs.length} ready document(s) selected.`
                                : `Leave empty and we'll auto-include all ${readyDocs.length} ready document(s) from this matter.`}
                        </div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                            {docs.map((d) => {
                                const isReady = d.processing_status === "ready";
                                return (
                                    <label key={d.id} className={`flex items-center gap-2 text-sm ${isReady ? "" : "opacity-60"}`}>
                                        <Checkbox
                                            disabled={!isReady}
                                            checked={v.include_document_ids.includes(d.id)}
                                            onCheckedChange={(c) => {
                                                const ids = new Set(v.include_document_ids);
                                                if (c) ids.add(d.id);
                                                else ids.delete(d.id);
                                                setV({ ...v, include_document_ids: Array.from(ids) });
                                            }}
                                            data-testid={`doc-checkbox-${d.id}`}
                                        />
                                        <span className="truncate">{d.original_filename}</span>
                                        <span className={`chip ml-auto ${isReady ? "chip-doc" : "chip-external"}`}>
                                            {isReady ? (d.doc_type || "doc") : d.processing_status}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}
            </Labeled>

            <Labeled label="Instructions">
                <div className="flex items-start gap-2">
                    <Textarea
                        rows={4}
                        data-testid="draft-instructions"
                        placeholder="e.g., Draft a reply to the legal notice dated 12.03.2024 denying all allegations."
                        value={v.instructions}
                        onChange={(e) => setV({ ...v, instructions: e.target.value })}
                    />
                    <div className="flex flex-col gap-1 shrink-0">
                        <VoiceButton compact onTranscript={({ text }) => setV({ ...v, instructions: text })} testid="draft-mic" />
                        <CitePrecedentButton
                            matter={matters.find((mm) => mm.id === v.matter_id)}
                            onInsert={(snippet) => setV({ ...v, instructions: (v.instructions ? v.instructions.trimEnd() + "\n\n" : "") + snippet })}
                        />
                    </div>
                </div>
            </Labeled>
            <div className="flex items-center gap-2">
                <Checkbox
                    id="strict"
                    checked={v.strict_mode}
                    onCheckedChange={(c) => setV({ ...v, strict_mode: !!c })}
                    data-testid="draft-strict"
                />
                <label htmlFor="strict" className="text-sm">Strict mode — use only facts from attached sources</label>
            </div>
            <div className="flex items-start gap-2 border border-amber-200 bg-amber-50/40 rounded-sm px-3 py-2">
                <Checkbox
                    id="senior-review"
                    checked={v.senior_review}
                    onCheckedChange={(c) => setV({ ...v, senior_review: !!c })}
                    data-testid="draft-senior-review"
                    className="mt-0.5"
                />
                <label htmlFor="senior-review" className="text-sm cursor-pointer">
                    <span className="inline-flex items-center gap-1.5 font-medium">
                        <Sparkle size={13} weight="fill" className="text-amber-600" />
                        Senior Counsel review
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                        Adds two extra LLM passes — a critique by a reviewer agent and an automatic redraft applying its suggestions. Adds ~30–60s. Recommended for{" "}
                        {SUGGESTED_REVIEW_TYPES.has(v.draft_type) ? <span className="font-medium text-foreground">this draft type</span> : "high-stakes filings"}.
                    </span>
                </label>
            </div>
            {loading && job?.stages && (
                <ProgressTimeline
                    stages={job.stages}
                    title={v.senior_review ? "Drafting + Senior review" : "Drafting"}
                    subtitle={v.senior_review ? "~60–120s total" : "~10–30s"}
                    data-testid="draft-progress"
                />
            )}
            <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background border-t border-border flex justify-end mt-2">
                <Button onClick={gen} disabled={loading} data-testid="draft-generate-btn">
                    {loading ? "Generating…" : "Generate draft"}
                </Button>
            </div>
        </div>
    );
}

function Labeled({ label, children }) {
    return (
        <div className="space-y-1.5">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
            {children}
        </div>
    );
}

function CitePrecedentButton({ matter, onInsert }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [list, setList] = useState([]);
    const [busy, setBusy] = useState(false);
    const debRef = useState({ t: null })[0];

    const load = async (query) => {
        setBusy(true);
        try {
            const params = {};
            if (query) params.q = query;
            // Prefer favourable + matter's practice area
            if (matter?.practice_area) params.practice_area = matter.practice_area;
            const rows = await precedentsApi.list(params);
            // Rank: favourable=true first, then by usage_count desc
            rows.sort((a, b) => {
                const av = (a.favourable === true ? 2 : a.favourable === false ? 0 : 1);
                const bv = (b.favourable === true ? 2 : b.favourable === false ? 0 : 1);
                if (av !== bv) return bv - av;
                return (b.usage_count || 0) - (a.usage_count || 0);
            });
            setList(rows);
        } catch { setList([]); }
        setBusy(false);
    };

    useEffect(() => {
        if (!open) return;
        load("");
    }, [open, matter?.practice_area]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) return;
        if (debRef.t) clearTimeout(debRef.t);
        debRef.t = setTimeout(() => load(q), 250);
        return () => clearTimeout(debRef.t);
    }, [q, open]); // eslint-disable-line react-hooks/exhaustive-deps

    const insert = async (p) => {
        const snippet = `Reliance is placed upon **${p.citation}**${p.title ? ` (${p.title})` : ""}${p.holding ? ` — ${p.holding}` : ""}.`;
        onInsert?.(snippet);
        try { await precedentsApi.use(p.id, matter?.id); } catch { /* non-fatal */ }
        toast.success(`Cited: ${p.citation}`);
        setOpen(false);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className="border border-border rounded-sm h-9 w-9 inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/40"
                    title="Cite a precedent"
                    data-testid="cite-precedent-btn"
                >
                    <Scales size={14} />
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Cite a precedent</DialogTitle>
                    <DialogDescription>
                        {matter?.practice_area
                            ? <>Showing precedents tagged <b>{matter.practice_area}</b> first. Favourable precedents ranked higher.</>
                            : "Pick a precedent to insert into the instructions."}
                    </DialogDescription>
                </DialogHeader>
                <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search citation / holding / statute / tag…" className="pl-8" data-testid="cite-prec-search" />
                </div>
                <div className="max-h-[55vh] overflow-y-auto space-y-2 mt-2" data-testid="cite-prec-list">
                    {busy && <div className="text-xs text-muted-foreground">Searching…</div>}
                    {!busy && !list.length && (
                        <div className="text-sm text-muted-foreground p-6 text-center">
                            No matching precedents.{" "}
                            <Link to="/precedents" className="underline" onClick={() => setOpen(false)}>Add some to your library →</Link>
                        </div>
                    )}
                    {list.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => insert(p)}
                            className="w-full text-left border border-border bg-white rounded-md p-3 hover:bg-muted/30"
                            data-testid={`cite-prec-${p.id}`}
                        >
                            <div className="flex items-center gap-2 flex-wrap text-xs">
                                {p.favourable === true && <ThumbsUp size={12} weight="fill" className="text-emerald-700" />}
                                <span className="font-mono">{p.citation}</span>
                                <span className="chip chip-doc">{(p.court || "").replace(/_/g, " ")}</span>
                                {p.year && <span className="text-muted-foreground">{p.year}</span>}
                                <span className="text-muted-foreground">used {p.usage_count}×</span>
                            </div>
                            {p.title && <div className="font-medium text-sm mt-1">{p.title}</div>}
                            {p.holding && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{p.holding}</div>}
                        </button>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
